import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Button, ButtonGroup, IconButton, Tooltip, Stack } from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import ConfirmDeleteDialog from "../ConfirmDeleteDialog";
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { global } from "../../context/global";
import { Capitalize } from "../../functions/Formatters";
import KPIDialog from "./KPIDialog";
import { deleteKPI } from "./ThemeActions";

// KPIOverviewComponent: displays a table of all KPIs belonging to a theme
export default function KPIOverviewComponent() {
    const { themeID } = useParams();
    const dispatch = useDispatch();
    const userIsAdmin = useSelector((state) => state.auth.account.is_staff);
    const theme = useSelector((state) => state.themes.themes.find((t) => Number(t.id) === Number(themeID)));

    const kpis = theme?.kpis || [];
    const subthemes = theme?.subthemes || [];

    const [kpiDialogOpen, setKPIDialogOpen] = useState(false);
    const [kpiToEdit, setKPIToEdit] = useState(false);
    const [kpiDeleteDialogOpen, setKPIDeleteDialogOpen] = useState(false);
    const [kpiToDelete, setKPIToDelete] = useState(false);

    const handleDialogClose = () => {
        setKPIDialogOpen(false);
        setKPIToEdit(false);
        setKPIDeleteDialogOpen(false);
        setKPIToDelete(false);
    };

    const handleClickEditKPI = (kpi) => {
        setKPIToEdit(kpi);
        setKPIDialogOpen(true);
    };

    const handleClickAddKPI = () => {
        setKPIDialogOpen(true);
    };

    const handleClickDeleteKPI = (kpi) => {
        setKPIToDelete(kpi);
        setKPIDeleteDialogOpen(true);
    };

    const tableHeaders = [
        Capitalize(global.kpi),
        Capitalize(global.description),
        Capitalize(global.unit || "Eenheid"),
        userIsAdmin ? Capitalize(global.actions || "Acties") : null,
    ].filter(Boolean);

    if (!theme) {
        return <Typography variant="body1">Thema niet gevonden.</Typography>;
    }

    return (
        <>
            <KPIDialog onClose={handleDialogClose} open={kpiDialogOpen} edit={kpiToEdit} theme={theme.id} />
            <ConfirmDeleteDialog
                open={kpiDeleteDialogOpen}
                onClose={handleDialogClose}
                dialogTitle={"KPI verwijderen"}
                dialogContentText={`Weet u zeker dat u KPI ${kpiToDelete?.name} wilt verwijderen?`}
                dialogConfirmButton={"Verwijder KPI"}
                handleConfirm={() => {
                    deleteKPI(kpiToDelete, dispatch, handleDialogClose);
                }}
            />
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                {userIsAdmin && (
                    <ButtonGroup variant="contained">
                        <Button startIcon={<Add />} onClick={handleClickAddKPI} >
                            KPI toevoegen
                        </Button>
                    </ButtonGroup>
                )}
            </Stack>
            {kpis.length === 0 ? (
                <Typography variant="body1">Er zijn nog geen KPI's toegevoegd aan dit thema.</Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {tableHeaders.map((header) => (
                                    <TableCell key={header}>
                                        <Typography variant="h5">{header}</Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {kpis.map((kpi) => (
                                <TableRow
                                    key={kpi.id}
                                    onClick={() => handleClickEditKPI(kpi)}
                                    sx={{
                                        cursor: "pointer",
                                        '&:hover td': (theme) => ({
                                            borderTop: `3px solid ${theme.palette.primary.main}`,
                                            borderBottom: `3px solid ${theme.palette.primary.main}`,
                                        }),
                                        '&:hover td:first-of-type': (theme) => ({
                                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                                            borderTopLeftRadius: 4,
                                            borderBottomLeftRadius: 4,
                                        }),
                                        '&:hover td:last-of-type': (theme) => ({
                                            borderRight: `3px solid ${theme.palette.primary.main}`,
                                            borderTopRightRadius: 4,
                                            borderBottomRightRadius: 4,
                                        }),
                                    }}

                                >
                                    <TableCell>
                                        <Typography fontSize="1.05rem">{kpi.name}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography color="text.secondary">
                                            {kpi.description || "Geen beschrijving"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography>{kpi.unit || "-"}</Typography>
                                    </TableCell>
                                    {userIsAdmin && (
                                        <TableCell width={120} align="right" onClick={(e) => e.stopPropagation()}>
                                            <Tooltip title="KPI bewerken">
                                                <IconButton size="small" onClick={() => handleClickEditKPI(kpi)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="KPI verwijderen">
                                                <IconButton size="small" onClick={() => handleClickDeleteKPI(kpi)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    );
}
